import { AiOutlined } from '@duxton-web/icons';
import {
  Checkbox,
  DatePicker,
  Flex,
  Modal,
  Select,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';

import { langLabel } from './adapters';
import { contentConfigModalStyle } from './theme';

interface Props {
  attributes: string[];
  initialAttributes: string[];
  initialLanguage: string;
  isRegeneration: boolean;
  languages: string[];
  open: boolean;
  onCancel: () => void;
  onGenerate: (attributes: string[], dateRange: [Dayjs, Dayjs], language: string) => void;
}

const nextSevenDays = (): [Dayjs, Dayjs] => [
  dayjs().add(1, 'day'),
  dayjs().add(7, 'day'),
];
const initialDateRange = (): [Dayjs | null, Dayjs | null] => nextSevenDays();
const EMPTY_ATTRIBUTES: string[] = [];

const ContentGenerationSetupModal = ({
  attributes,
  initialAttributes,
  initialLanguage,
  isRegeneration,
  languages,
  open,
  onCancel,
  onGenerate,
}: Props) => {
  const [selectedAttributes, setSelectedAttributes] = useState(EMPTY_ATTRIBUTES);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [dateRange, setDateRange] = useState(initialDateRange());

  useEffect(() => {
    if (!open) return;
    const availableAttributes = new Set(attributes);
    const initialSelection = initialAttributes.filter((attribute) =>
      availableAttributes.has(attribute));
    setSelectedAttributes(initialSelection.length > 0 ? initialSelection : [...attributes]);
    let defaultLanguage = languages[0] || '';
    if (languages.includes('en')) defaultLanguage = 'en';
    if (languages.includes(initialLanguage)) defaultLanguage = initialLanguage;
    setSelectedLanguage(defaultLanguage);
    setDateRange(nextSevenDays());
  }, [attributes, initialAttributes, initialLanguage, languages, open]);

  const allSelected = attributes.length > 0
    && selectedAttributes.length === attributes.length;
  const someSelected = selectedAttributes.length > 0 && !allSelected;
  const ready = selectedAttributes.length > 0
    && Boolean(selectedLanguage)
    && Boolean(dateRange[0] && dateRange[1]);
  const submit = () => {
    if (!dateRange[0] || !dateRange[1] || !selectedLanguage || selectedAttributes.length === 0) return;
    onGenerate(selectedAttributes, [dateRange[0], dateRange[1]], selectedLanguage);
  };

  return (
    <Modal
      title={(
        <Flex align="center" gap={8}>
          <AiOutlined />
          <span>{isRegeneration ? 'Regenerate content' : 'Generate content'}</span>
        </Flex>
      )}
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText={isRegeneration ? 'Regenerate content' : 'Generate content'}
      okButtonProps={{ disabled: !ready }}
      width={520}
      style={contentConfigModalStyle}
      className="cc-modal cc-generation-setup-modal"
      destroyOnClose
    >
      <Flex vertical gap="large">
        <Flex vertical gap={8}>
          <Typography.Body level={2} strong>Language</Typography.Body>
          <Select
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            options={languages.map((language) => ({
              value: language,
              label: langLabel(language),
            }))}
            style={{ width: '100%' }}
          />
        </Flex>

        <Flex vertical gap={8}>
          <Typography.Body level={2} strong>Attributes</Typography.Body>
          <Typography.Metadata type="secondary">
            Mystique will generate a separate set of results for each selected attribute.
          </Typography.Metadata>
          <div className="cc-generation-attribute-picker">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(event) => setSelectedAttributes(
                event.target.checked ? [...attributes] : [],
              )}
            >
              Select all
            </Checkbox>
            <div className="cc-generation-attribute-divider" />
            <Checkbox.Group
              value={selectedAttributes}
              onChange={(values: string[]) => setSelectedAttributes(values)}
              className="cc-generation-attribute-options"
            >
              {attributes.map((attribute) => (
                <Checkbox key={attribute} value={attribute}>{attribute}</Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </Flex>

        <Flex vertical gap={8}>
          <Typography.Body level={2} strong>Date range</Typography.Body>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates ? [dates[0], dates[1]] : [null, null]);
            }}
            disabledDate={(date) => date && date.isBefore(dayjs(), 'day')}
            format="MM/DD/YYYY"
            placeholder={['Start date', 'End date']}
            style={{ width: '100%' }}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ContentGenerationSetupModal;
